/*
 * PF for Outlook — task pane bridge.
 *
 * Thin Office.js bridge between the Outlook mail client and the Wippy chat that
 * runs in the embedded iframe. Unlike the Word plugin, this does NOT stream mail
 * content: the Wippy agent reaches the mailbox server-side via Microsoft Graph.
 * The bridge only:
 *   1. reads the OPEN item's identifiers (anchor) and hands them to the iframe, and
 *   2. writes an agent-drafted reply into the LIVE compose window (`insert_draft`).
 *   3. attaches a file Fortress generated to the draft or a new message (`deliver_file`),
 *      since the pane cannot download.
 *
 * Protocol mirrors the Word add-in: a single `{ type: "@office-plugin", action, ...}`
 * envelope, both directions, over postMessage against CHAT_ORIGIN.
 */

/// <reference types="office-js" />

/* global document, Office, window, console, atob */

import { acquireGraphToken, isNaaSupported, setMsalLogSink } from "../auth/naa";
import { replaceAboveQuotedChain, sameConversation } from "./quoted-chain";
import { bodySig } from "./body-sig";
import { deliverFile } from "./deliver-file";

declare const WIPPY_IFRAME_URL: string;
declare const WIPPY_VIEW_ID: string;
declare const AUTH_DIAG: boolean;
declare const MS_CLIENT_ID: string;

/** Allowed origin for the embedded chat (must match iframe src). */
const CHAT_ORIGIN = new URL(WIPPY_IFRAME_URL).origin;

/**
 * The iframe src: the Wippy auth-bootstrap page carrying the target view as a
 * `view` query param. The bootstrap opens that view.page after auth; the view's
 * init_agent hardcodes the agent, so the add-in does not select it.
 */
function buildIframeSrc(): string {
  try {
    const u = new URL(WIPPY_IFRAME_URL);
    if (WIPPY_VIEW_ID) u.searchParams.set("view", WIPPY_VIEW_ID);
    return u.toString();
  } catch {
    return WIPPY_IFRAME_URL;
  }
}

let wsReady = false;
let anchorSent = false;

/** The open-item context handed to the agent session. */
interface OutlookAnchor {
  mode: "read" | "compose";
  itemType: string | null;
  itemId: string | null; // Graph REST id when available (null for a new compose)
  conversationId: string | null;
  internetMessageId: string | null;
  subject: string | null;
  from: string | null;
  to: string[];
  cc: string[];
}

Office.onReady((info) => {
  if (info.host !== Office.HostType.Outlook) return;

  (document.getElementById("chat-iframe") as HTMLIFrameElement).src = buildIframeSrc();
  document.getElementById("sideload-msg").style.display = "none";
  document.getElementById("app-body").style.display = "flex";

  initOutlookBridge();

  if (AUTH_DIAG) runAuthDiagnostic();

  // Poll the iframe until its WebSocket to Wippy is ready (== OAuth complete),
  // exactly like the Word shell. Once ready we push the open-item anchor.
  const wsCheckInterval = setInterval(() => {
    if (wsReady) {
      clearInterval(wsCheckInterval);
      return;
    }
    notifyIframe({ type: "@office-plugin", action: "check_ws_status" });
  }, 500);
});

/**
 * Outlook Bridge — two-way communication between Outlook and Wippy.
 *
 * Outlook → Wippy: on load / item change we send the open item's ids as
 *   `outlook_item` so the agent session can anchor on it.
 * Wippy → Outlook: the agent's reply arrives as `insert_draft` and we write it
 *   into the live compose body (or open a pre-filled reply in read mode).
 */
function initOutlookBridge(): void {
  // Re-read the anchor whenever the user switches messages. NOTE: ItemChanged
  // only fires for a PINNED task pane, and pinning requires a Microsoft 365
  // (Exchange Online) account — it is unavailable on personal Outlook.com
  // accounts. See docs/dynamic-item-reanchor-plan.md.
  Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, () => {
    sendAnchor(true);
  });

  window.addEventListener("message", (event: MessageEvent) => {
    if (event.origin !== CHAT_ORIGIN) return;

    const data = typeof event.data === "string" ? safeParse(event.data) : event.data;
    if (!data || data.type !== "@office-plugin") return;

    switch (data.action) {
      case "ws_connected":
        // WebSocket connected in the iframe = OAuth complete.
        wsReady = true;
        sendAnchor();
        // Tell the view whether we can self-serve Graph tokens, so it can skip
        // the Salesforce "Connect Outlook" gate entirely.
        notifyIframe({
          type: "@office-plugin",
          action: "ms_auth_available",
          supported: isNaaSupported(),
        });
        break;
      case "request_item":
        // Iframe asks us to (re)send the current anchor.
        sendAnchor(true);
        break;
      case "request_current_item":
        // Backend agent tool pulls the live open item (running-session re-anchor).
        sendCurrentItem(data.request_id, data.reply_pid, data.include_body === true);
        break;
      case "insert_draft":
        insertDraft(data);
        break;
      case "request_ms_token":
        // The launcher view needs a Graph token to hand to the backend. It lives
        // in a nested iframe, so reply to the window that asked, not the chrome.
        sendMsToken(
          data.request_id,
          data.reply_pid,
          data.force === true,
          event.source as Window | null
        );
        break;
      case "open_external":
        // Open a full Fortress view (e.g. Automations) in the user's real browser.
        openExternal(data.url);
        break;
      case "host_info":
        // A view asks which add-in it runs in (the document preview turns its
        // Download button into "Attach to email" here). The Word add-in does not
        // answer, so views keep their Word behavior there.
        notifyIframe(
          {
            type: "@office-plugin",
            action: "host_info_reply",
            request_id: data.request_id,
            host: "outlook",
            compose: isComposeItem(Office.context.mailbox.item),
          },
          event.source as Window | null
        );
        break;
      case "deliver_file": {
        // Attach a generated file (presigned link) to the open draft, or to a new
        // message; answer the view either way so it never sits on a dead button.
        const target = event.source as Window | null;
        deliverFile(Office.context.mailbox, data.url, data.filename).then((result) =>
          notifyIframe(
            { type: "@office-plugin", action: "deliver_file_result", request_id: data.request_id, ...result },
            target
          )
        );
        break;
      }
    }
  });
}

/**
 * Open a URL in the user's default system browser.
 *
 * The iframe cannot do this itself: in DESKTOP Outlook a plain `window.open` from
 * an add-in lands in an embedded webview with no Fortress session, so a full app
 * view (Automations) renders as a blank white window. `Office.context.ui.
 * openBrowserWindow` hands the URL to the real default browser, where the user's
 * Fortress cookies live. Falls back to `window.open` (fine in OWA/browser).
 */
function openExternal(url: string): void {
  if (!url) return;
  try {
    if (Office?.context?.ui && typeof Office.context.ui.openBrowserWindow === "function") {
      Office.context.ui.openBrowserWindow(url);
      return;
    }
  } catch (err: any) {
    console.warn("openBrowserWindow failed, falling back to window.open:", err?.message ?? err);
  }
  window.open(url, "_blank", "noopener");
}

/** Read the open item's anchor and post it to the iframe. */
async function sendAnchor(force = false): Promise<void> {
  if (anchorSent && !force) return;
  const item = Office.context.mailbox.item as any;
  if (!item) {
    // No item selected (folder view / cleared selection): reset the launcher card.
    anchorSent = false;
    notifyIframe({ type: "@office-plugin", action: "outlook_item", item: null });
    return;
  }
  try {
    const anchor = await readAnchor(item);
    anchorSent = true;
    notifyIframe({ type: "@office-plugin", action: "outlook_item", item: anchor });
  } catch (err: any) {
    console.warn("Failed to read Outlook item:", err?.message ?? err);
  }
}

/**
 * Answer a backend pull for the live open item (running-session re-anchor).
 * Always replies — a null item lets the tool report "no message open" instead
 * of timing out.
 */
async function sendCurrentItem(
  requestId: string,
  replyPid: string,
  includeBody = false
): Promise<void> {
  const item = Office.context.mailbox.item as any;
  let anchor: (OutlookAnchor & { body_html?: string; body_sig?: string }) | null = null;
  if (item) {
    try {
      anchor = await readAnchor(item);
      // The backend edits text inside the open body (e.g. answers in the quoted chain),
      // so it asks for the body and its fingerprint along with the anchor.
      if (anchor && includeBody) {
        const html = await readBodyHtml(item);
        if (html !== null) {
          anchor.body_html = html;
          anchor.body_sig = bodySig(html);
        }
      }
    } catch (err: any) {
      console.warn("Failed to read current Outlook item:", err?.message ?? err);
    }
  }
  notifyIframe({
    type: "@office-plugin",
    action: "current_item",
    item: anchor,
    request_id: requestId,
    reply_pid: replyPid,
  });
}

/** Verbose MSAL lines captured during the auth self-test. */
const msalLog: string[] = [];

/**
 * Production MSAL is minified, so its logger emits short keys ("1yb4fi") rather
 * than sentences. The package ships a reverse mapping but its `exports` field
 * blocks importing it, so keys are shown raw and decoded off-device against
 * node_modules/@azure/msal-browser/lib/log-strings-mapping.json.
 */
async function decodeMsalLog(lines: string[]): Promise<string> {
  return lines.length ? lines.join("\n") : "(no MSAL log lines captured)";
}

/**
 * Render everything a thrown value carries.
 *
 * MSAL puts its detail on `errorCode`/`errorMessage`/`subError`, and some
 * rejections carry no `message` at all — reading only `message` collapses a real
 * failure to an empty string, which tells nobody anything.
 */
function describeError(err: any): string {
  if (err == null) return "(threw null/undefined)";
  if (typeof err !== "object") return `${typeof err}: ${String(err)}`;

  const out: string[] = [];
  const named = ["name", "errorCode", "errorMessage", "subError", "message", "correlationId"];
  for (const k of named) {
    const v = (err as any)[k];
    if (v !== undefined && v !== null && String(v) !== "") out.push(`${k}: ${String(v)}`);
  }

  // Anything not covered above — MSAL error shapes vary by version and flow.
  try {
    const own = Object.getOwnPropertyNames(err).filter((k) => !named.includes(k) && k !== "stack");
    for (const k of own) {
      const v = (err as any)[k];
      if (typeof v !== "function" && v !== undefined && String(v) !== "") {
        out.push(`${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`);
      }
    }
  } catch {
    /* non-enumerable or circular — the named fields above are enough */
  }

  if (!out.length) {
    try {
      out.push("raw: " + JSON.stringify(err));
    } catch {
      out.push("raw: " + Object.prototype.toString.call(err));
    }
  }

  const stackLines = String((err as any).stack ?? "").split("\n");
  const stack = stackLines.slice(0, 3).join("\n");
  if (stack) out.push("stack: " + stack);
  return out.join("\n");
}

/** Map a rendered error blob to the concrete next action, when we can tell. */
function hintFor(blob: string): string {
  const code = blob.match(/AADSTS\d+/)?.[0];
  if (code === "AADSTS50011" || /redirect|reply url/i.test(blob)) {
    return (
      "→ Redirect URI not registered. Add an SPA redirect URI\n" +
      "  brk-multihub://" +
      window.location.host +
      "\n  to the Entra app registration."
    );
  }
  if (code === "AADSTS700016" || /not found in the directory/i.test(blob)) {
    return "→ This client ID does not exist in the signed-in user's tenant.";
  }
  if (code === "AADSTS650057" || /consent/i.test(blob)) {
    return "→ Missing Graph permissions or admin consent on the app registration.";
  }
  if (/nested app auth|naa|not supported|unsupported/i.test(blob)) {
    return "→ This Outlook host cannot broker NAA. Try current desktop Outlook or OWA.";
  }
  return "→ No AADSTS code. Screenshot this whole panel.";
}

/**
 * One-shot NAA self-test, rendered into the task pane.
 *
 * Exists so the Entra app registration can be verified by opening the add-in
 * rather than by attaching a debugger to it — the token can only be brokered by
 * a real signed-in Outlook host, so this check cannot be run anywhere else.
 * Compiled out unless the bundle is built with AUTH_DIAG=1.
 */
async function runAuthDiagnostic(): Promise<void> {
  const el = document.getElementById("auth-diag");
  if (!el) return;
  el.style.maxHeight = "70vh";
  el.style.overflowY = "auto";

  // Capture MSAL's own log — the bridge's rejection reason appears nowhere else.
  msalLog.length = 0;
  setMsalLogSink((level, message) => {
    if (/^(Error|Warning)$/.test(level) || /bridge|nested|token|redirect|scope/i.test(message)) {
      msalLog.push(`[${level}] ${message}`);
    }
  });

  // Always report the environment, so a failure can be told apart from a
  // misconfigured build (wrong client id, wrong origin, NAA unavailable).
  let hostName = "?";
  let platform = "?";
  try {
    hostName = String(Office?.context?.mailbox?.diagnostics?.hostName ?? "?");
    platform = String(Office?.context?.platform ?? "?");
  } catch {
    /* diagnostics unavailable on some hosts */
  }
  const env = [
    `client:   ${MS_CLIENT_ID}`,
    `origin:   ${window.location.host}`,
    `host:     ${hostName} / ${platform}`,
    `NAA:      ${isNaaSupported()}`,
  ].join("\n");

  el.style.display = "block";
  el.style.background = "#fff4ce";
  el.textContent = env + "\n\nrequesting token…";

  if (!isNaaSupported()) {
    el.style.background = "#fde7e9";
    el.textContent =
      env +
      "\n\nFAIL — this Outlook host does not support nested app authentication.\n" +
      "Try current Outlook desktop or Outlook on the web.";
    return;
  }

  try {
    const { accessToken, expiresOn } = await acquireGraphToken();
    // Decode the JWT payload only to display audience/scopes. No verification —
    // this is a human-readable report, not a security decision.
    const claims = JSON.parse(
      decodeURIComponent(
        atob(accessToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
    );
    const rawScopes = String(claims.scp ?? "");
    const scopes: string[] = rawScopes.split(" ").filter(Boolean);
    const wanted = [
      "User.Read",
      "Mail.ReadWrite",
      "Mail.Send",
      "Calendars.ReadWrite",
      "Calendars.Read.Shared",
      "Contacts.Read",
      "People.Read",
    ];
    const missing = wanted.filter((s) => !scopes.some((g) => g.toLowerCase() === s.toLowerCase()));

    const lines = [
      missing.length ? "PARTIAL — token acquired, scopes incomplete" : "PASS — token acquired",
      `aud:     ${claims.aud}`,
      `tenant:  ${claims.tid}`,
      `user:    ${claims.upn ?? claims.preferred_username ?? "?"}`,
      `expires: ${new Date(expiresOn).toLocaleTimeString()}`,
      `scopes(${scopes.length}): ${scopes.join(", ")}`,
    ];
    if (missing.length) lines.push(`MISSING: ${missing.join(", ")}`);

    el.style.background = missing.length ? "#fff4ce" : "#dff6dd";
    el.textContent = env + "\n\n" + lines.join("\n");
  } catch (err: any) {
    el.style.background = "#fde7e9";
    const blob = describeError(err);

    // Do NOT auto-launch the interactive flow: a popup thrown up the instant the
    // pane loads gets dismissed, and the broker reports that dismissal as
    // user_canceled — which tells us nothing about whether Entra would have
    // accepted the request. Put it behind a button the user drives instead.
    el.textContent =
      env +
      "\n\nFAIL (silent)\n" +
      blob +
      "\n\n" +
      hintFor(blob) +
      "\n\n--- MSAL log ---\n" +
      (await decodeMsalLog(msalLog.slice(-40))) +
      "\n\n";

    const btn = document.createElement("button");
    btn.textContent = "Sign in to Microsoft →";
    btn.style.cssText =
      "font: 600 12px system-ui; padding: 8px 14px; margin-top: 4px; cursor: pointer;" +
      "border: 0; border-radius: 4px; background: #0f6cbd; color: #fff;";
    const status = document.createElement("div");
    status.style.cssText = "margin-top: 8px; white-space: pre-wrap;";

    btn.onclick = async () => {
      btn.disabled = true;
      status.textContent =
        "Opening sign-in… complete it fully, including any\n" +
        '"permissions requested" / consent screen. Do not close the window.';
      try {
        const { acquireTokenPopupForDiagnostic } = await import("../auth/naa");
        const r = await acquireTokenPopupForDiagnostic();
        el.style.background = "#dff6dd";
        status.textContent =
          "INTERACTIVE SUCCEEDED — consent was the blocker.\n" +
          "Token expires " +
          new Date(r.expiresOn).toLocaleTimeString() +
          ".\nClose and reopen the pane: silent should now work.";
      } catch (perr: any) {
        status.textContent =
          "INTERACTIVE FAILED\n" +
          describeError(perr) +
          "\n\n" +
          hintFor(describeError(perr)) +
          "\n\n--- MSAL log ---\n" +
          (await decodeMsalLog(msalLog.slice(-40)));
        btn.disabled = false;
      }
    };

    el.appendChild(btn);
    el.appendChild(status);
  }
}

/**
 * Answer the launcher view's pull for a Microsoft Graph access token.
 *
 * `target` is the window that asked — the view, nested one level below the host
 * chrome; sending to the chrome instead drops the reply. `force` is the
 * backend's post-401 recovery path: re-acquire rather than replay a dead token.
 * Always replies, even on failure, so the view falls through promptly instead of
 * sitting out its 8s timeout behind a blank gate, and a failed acquire surfaces
 * to the agent as a sign-in-required error.
 */
async function sendMsToken(
  requestId: string,
  replyPid: string,
  force = false,
  target?: Window | null
): Promise<void> {
  try {
    const { accessToken, expiresOn } = await acquireGraphToken(force);
    notifyIframe(
      {
        type: "@office-plugin",
        action: "ms_token",
        access_token: accessToken,
        expires_on: expiresOn,
        request_id: requestId,
        reply_pid: replyPid,
      },
      target
    );
  } catch (err: any) {
    console.warn("Failed to acquire Graph token:", err?.message ?? err);
    notifyIframe(
      {
        type: "@office-plugin",
        action: "ms_token",
        access_token: null,
        error: String(err?.message ?? err),
        request_id: requestId,
        reply_pid: replyPid,
      },
      target
    );
  }
}

/** Compose items expose write methods on body (`setAsync`); read items do not. */
function isComposeItem(item: any): boolean {
  return !!(item && item.body && typeof item.body.setAsync === "function");
}

async function readAnchor(item: any): Promise<OutlookAnchor> {
  const itemType: string | null = item.itemType ?? null;

  if (!isComposeItem(item)) {
    // Read mode: subject/from/to/cc are plain values, not async getters.
    return {
      mode: "read",
      itemType,
      itemId: toRestId(item.itemId),
      conversationId: item.conversationId ?? null,
      internetMessageId: item.internetMessageId ?? null,
      subject: item.subject ?? null,
      from: item.from?.emailAddress ?? item.organizer?.emailAddress ?? null,
      to: mapAddresses(item.to),
      cc: mapAddresses(item.cc),
    };
  }

  // Compose mode: subject/to/cc are async getters; itemId is usually null.
  const [subject, to, cc] = await Promise.all([
    getAsync<string>(item.subject),
    getRecipientsAsync(item.to),
    getRecipientsAsync(item.cc),
  ]);
  return {
    mode: "compose",
    itemType,
    itemId: toRestId(item.itemId),
    conversationId: item.conversationId ?? null,
    internetMessageId: item.internetMessageId ?? null,
    subject,
    from: null,
    to,
    cc,
  };
}

function readBodyHtml(item: any): Promise<string | null> {
  return new Promise((resolve) => {
    if (!item?.body || typeof item.body.getAsync !== "function") {
      resolve(null);
      return;
    }
    item.body.getAsync(Office.CoercionType.Html, (r: Office.AsyncResult<string>) =>
      resolve(r.status === Office.AsyncResultStatus.Succeeded ? r.value || "" : null)
    );
  });
}

/** Write an agent-drafted reply into the live message. */
function insertDraft(msg: any): void {
  const item = Office.context.mailbox.item as any;
  const html = msg.html ?? msg.text ?? "";
  if (!item) return;

  const ack = (success: boolean, reason?: string) =>
    notifyIframe({
      type: "@office-plugin",
      action: "insert_draft_result",
      success,
      reason,
      request_id: msg.request_id,
      reply_pid: msg.reply_pid,
    });

  // The agent names the thread it drafted for. If the open item is a different
  // conversation (the user switched windows since the backend checked), write
  // nothing: a reply for thread A must never land in a window replying to thread B.
  if (msg.conversation_id && !sameConversation(msg.conversation_id, item.conversationId)) {
    ack(false);
    return;
  }

  if (isComposeItem(item)) {
    // Recipients / subject. recipient_mode "add" (default) APPENDS via addAsync;
    // "replace" overwrites via setAsync. Append is the safe default: setAsync
    // silently discards everyone already in the field, so "add Jane to cc" must
    // never call setAsync — doing so was wiping the existing recipient list.
    const recipientMode = msg.recipient_mode === "replace" ? "replace" : "add";
    if (msg.subject) item.subject.setAsync(msg.subject);
    applyRecipients(item.to, msg.to, recipientMode);
    applyRecipients(item.cc, msg.cc, recipientMode);

    // Body. body_mode:
    //   "full"             → the whole body, already edited by the backend (expect_sig guards it)
    //   "replace"          → overwrite everything ABOVE the quoted chain (the chain stays)
    //   "delete_selection" → clear the current selection
    //   "cursor" (default) → insert at the cursor, replacing any selection
    // A recipients/subject-only edit (no html, no explicit delete) leaves the
    // body untouched — previously an empty html still stamped a zero-width space
    // at the cursor. "replace" is what a rewrite needs: setSelectedDataAsync with
    // nothing selected inserts at the cursor, which stacked a second copy on top
    // of the original instead of swapping it.
    const bodyMode: string = msg.body_mode || "cursor";
    const hasHtml = typeof html === "string" && html !== "";

    if (bodyMode === "full") {
      // The backend sends the WHOLE body after editing the one it read (edits inside the
      // quoted chain). Write it only if the body is still what it edited.
      item.body.getAsync(Office.CoercionType.Html, (read: Office.AsyncResult<string>) => {
        if (read.status !== Office.AsyncResultStatus.Succeeded) {
          ack(false, "unreadable");
          return;
        }
        if (msg.expect_sig && bodySig(read.value || "") !== msg.expect_sig) {
          ack(false, "changed");
          return;
        }
        item.body.setAsync(
          html,
          { coercionType: Office.CoercionType.Html },
          (r: Office.AsyncResult<void>) => ack(r.status === Office.AsyncResultStatus.Succeeded)
        );
      });
    } else if (bodyMode === "replace") {
      // Body.setAsync overwrites the WHOLE body, quoted chain included — so read the
      // body, swap only what sits above the chain, and write that back. If the body
      // looks like it carries a chain we cannot place, write nothing.
      item.body.getAsync(Office.CoercionType.Html, (read: Office.AsyncResult<string>) => {
        if (read.status !== Office.AsyncResultStatus.Succeeded) {
          ack(false);
          return;
        }
        const merged = replaceAboveQuotedChain(read.value || "", html);
        if (merged === null) {
          ack(false);
          return;
        }
        item.body.setAsync(
          merged,
          { coercionType: Office.CoercionType.Html },
          (r: Office.AsyncResult<void>) => ack(r.status === Office.AsyncResultStatus.Succeeded)
        );
      });
    } else if (bodyMode === "delete_selection") {
      // Outlook has no delete-selection API — replace the selection with a
      // zero-width space, which reads as nothing to the user.
      item.body.setSelectedDataAsync(
        String.fromCharCode(0x200b),
        { coercionType: Office.CoercionType.Text },
        (r: Office.AsyncResult<void>) => ack(r.status === Office.AsyncResultStatus.Succeeded)
      );
    } else if (hasHtml) {
      // Insert at the cursor in the open compose body; fall back to prepend.
      item.body.setSelectedDataAsync(
        html,
        { coercionType: Office.CoercionType.Html },
        (r: Office.AsyncResult<void>) => {
          const okd = r.status === Office.AsyncResultStatus.Succeeded;
          if (!okd) {
            item.body.prependAsync(html, { coercionType: Office.CoercionType.Html });
          }
          ack(okd);
        }
      );
    } else {
      // Recipients/subject-only edit — nothing to write to the body.
      ack(true);
    }
  } else if (typeof item.displayReplyForm === "function") {
    // Read mode: open a reply form pre-filled with the draft (recipients from the original).
    const openReplyAll =
      msg.reply_mode === "reply_all" && typeof item.displayReplyAllForm === "function";
    if (openReplyAll) {
      item.displayReplyAllForm({ htmlBody: html });
    } else {
      item.displayReplyForm({ htmlBody: html });
    }
    ack(true);
  }
}

/* ── Helpers ──────────────────────────────────────────────────────────────── */

function mapAddresses(list: any): string[] {
  return Array.isArray(list) ? list.map((r: any) => r.emailAddress).filter(Boolean) : [];
}

/**
 * Add or replace recipients on a compose field (To/Cc).
 * Append (`addAsync`) is the default and the safe one — it keeps everyone already
 * in the field. `replace` (`setAsync`) overwrites the whole list; use it only when
 * the caller explicitly wants the field reset, because setAsync silently discards
 * the current recipients. Falls back to setAsync if addAsync is unavailable.
 */
function applyRecipients(field: any, list: any, mode: "add" | "replace"): void {
  if (!field || !Array.isArray(list) || list.length === 0) return;
  if (mode === "replace" && typeof field.setAsync === "function") {
    field.setAsync(list);
  } else if (typeof field.addAsync === "function") {
    field.addAsync(list);
  } else if (typeof field.setAsync === "function") {
    field.setAsync(list);
  }
}

/** EWS ids from read mode need converting to Graph REST ids for server-side use. */
function toRestId(itemId: string | null | undefined): string | null {
  if (!itemId) return null;
  try {
    return Office.context.mailbox.convertToRestId(itemId, Office.MailboxEnums.RestVersion.v2_0);
  } catch {
    return itemId;
  }
}

function getAsync<T>(field: any): Promise<T | null> {
  return new Promise((resolve) => {
    if (!field || typeof field.getAsync !== "function") {
      resolve(null);
      return;
    }
    field.getAsync((r: Office.AsyncResult<T>) =>
      resolve(r.status === Office.AsyncResultStatus.Succeeded ? r.value : null)
    );
  });
}

function getRecipientsAsync(field: any): Promise<string[]> {
  return new Promise((resolve) => {
    if (!field || typeof field.getAsync !== "function") {
      resolve([]);
      return;
    }
    field.getAsync((r: Office.AsyncResult<Office.EmailAddressDetails[]>) =>
      resolve(r.status === Office.AsyncResultStatus.Succeeded ? mapAddresses(r.value) : [])
    );
  });
}

function safeParse(s: string): any {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

/**
 * Post into the Wippy frames. Defaults to the chat iframe — the host chrome —
 * which is the right target for anything the chrome itself relays (the anchor,
 * insert_draft, WS-routed results).
 *
 * `target` overrides it for a direct reply. The chrome embeds the launcher view
 * in a SECOND iframe, so a request that came from the view must be answered on
 * the view's own window; sending it to the chrome drops it, because the chrome's
 * bridge has no case for it.
 */
function notifyIframe(message: Record<string, unknown>, target?: Window | null): void {
  if (target) {
    target.postMessage(message, CHAT_ORIGIN);
    return;
  }
  const iframe = document.getElementById("chat-iframe") as HTMLIFrameElement;
  iframe?.contentWindow?.postMessage(message, CHAT_ORIGIN);
}
